import type { LazyExoticComponent } from "react";
import { lazy } from "react";

import type { BadgeProps } from "@/shared/PostMatchBadge.js";

export const SEARCH_PARAMS = {
  PLATFORM: "platform",
  GAMEMODE: "gamemode",
  ROLE: "role",
};

export const PLATFORMS = {
  PC: "pc",
  CONSOLE: "console",
} as const;

export const GAME_MODES = {
  QUICKPLAY: "quick",
  SUMMARY: "summary", // Marvel Rivals API probably uses this as the average for all Competitive ranks
  // Below are Competitive game modes
  BRONZE: "bronze",
  SILVER: "silver",
  GOLD: "gold",
  PLATINUM: "platinum",
  DIAMOND: "diamond",
  MASTER: "master", // Marvel Rivals API indicates this is the proper name but in the UI it says "Grand Master"
} as const;

export const ROLES = {
  ALL: "all",
  DEFENDER: "vanguard",
  DUELIST: "duelist",
  STRATEGIST: "strategist",
} as const;

/**
 * Todo: Refactor this into its own file since this gets imported globally from seo-routes
 */
export const HEROES_TO_ARTICLE_ID = {
  Hulk: "3XRjKiO6zu6Bw0QF7SnlJr",
  "Doctor Strange": "2qDKv3bI47h8iUdr4RqEBi",
  "Captain America": "7jZjyy2wOmbVDodsCHo91h",
  Groot: "5TX9zLgYf2GEwIH7g5DAXq",
  Venom: "632GrpjFKgql803kONtczz",
  Magneto: "zYjCQpPfTWo1AegI9EsJ0",
  Thor: "2YPmwmyZ7MzxXjEQxeP7ut",
  "Peni Parker": "2e9s4TenoUizAAJCw7hQtP",
  "The Punisher": "1Eb1o2lyQsJrCeRVkCw0fV",
  Storm: "E0HSr1NoN532IvniuEw3Y",
  Hawkeye: "6v8Wy9u8yizHitM5uXWQr9",
  Hela: "75mrwxqu6ckgqmjhmq6yxH",
  "Black Panther": "PBprAEFJtcJnMsS7vYjFR",
  Magik: "7rQ0lFUy5eFMWb3amwyDBH",
  "Moon Knight": "52opBvN82uVxUChABMOgJN",
  "Squirrel Girl": "4iRT9agTdeOCSjImGq61Ax",
  "Black Widow": "5srR9IYb8URUaiBJeK96Ol",
  "Iron Man": "Bb2D0RkuW9TcIBxdiHE6J",
  "Spider-Man": "2Lxi8oVFs2mF28FPC2dkkH",
  "Scarlet Witch": "vX7quxEOxMGdJ5VIfHj2z",
  "Winter Soldier": "46vonMdhhUaiKLzkdTzNgG",
  "Star-Lord": "67UF0cfEw1COYQUet7GsM6",
  Namor: "rIVR4OAi6nUBAAsSZEiUg",
  Psylocke: "mSSmEivFpy9acKwEPA581",
  Wolverine: "6n9io7YKYtkGBCRUrmiSvn",
  "Iron Fist": "9lhFlAJf6484cQUxF2zSp",
  Loki: "4NatxaZs0IpZe1xxSwM5Gt",
  Mantis: "5WiIKfxZhcFc6mhWuy1twH",
  "Rocket Raccoon": "3Mh7BFiFBjxzjQWfAWWKC1",
  "Cloak & Dagger": "o89uKVywEDGamTHnWPyHc",
  "Luna Snow": "7g35ZNsikdcnuddONtpEPv",
  "Adam Warlock": "7qNlGulzcuki0mhsKwcXT6",
  "Jeff the Land Shark": "3uFTiYAEwH4nyuNyMkEMa8",
  "Mister Fantastic": "3XkJHPQqCeI3Yg2tJHjXEf",
  "Invisible Woman": "3LK4JRZyhPFn7U2JQN1sv0",
};

export enum MatchTileTabsEnum {
  Scoreboard = "scoreboard",
  Statistics = "statistics",
  BannedHeroes = "bans",
}

export const ROLE_TRANSLATIONS: Record<
  (typeof ROLES)[keyof typeof ROLES],
  Translation
> = {
  [ROLES.ALL]: ["marvelrivals:role.all", "All"],
  [ROLES.DEFENDER]: ["marvelrivals:role.vanguard", "Vanguard"],
  [ROLES.DUELIST]: ["marvelrivals:role.duelist", "Duelist"],
  [ROLES.STRATEGIST]: ["marvelrivals:role.strategist", "Strategist"],
};

// BE + RE Mapped IDs
export const GAME_MODE_IDS = {
  1: ["marvelrivals:gameMode.quickPlay", "Quick Play"],
  2: ["marvelrivals:gameMode.competitive", "Competitive"],
  3: ["lol:queueTypes.custom", "Custom"],
  4: ["marvelrivals:gameMode.arcade", "Arcade"],
};

export const MAP_IDS_QUICKPLAY = {
  1309: "Krakoa", // Domination
  1034: "Shin Shibuya",
  1101: "Hall of Djalia",
  1217: "Central Park",
  1240: "Symbiotic Surface",
  1201: "Midtown",
  // 1235: "",
  1312: "Conquest / Doom Match",
} as const;

export const MAP_IDS_COMPETITIVE = {
  1245: "Spider Islands", // 1 Tokyo 2099
  1267: "Hall of Djalia", // 1
  1291: "Midtown", // 1
  1292: "Central Park", // 1
  1290: "Symbiotic Surface", // 1
  1272: "Birnin T'challa", // 1 Intergalactic Empire of Wakanda
  1231: "Yggdrasill Path", // 1 Yggsgard
  1118: "Sanctum Sanctorum",
  1230: "Shin Shibuya", // Tokyo 2099
  1310: "Krakoa", // 1
  1170: "Royale Palace", // Yggsgard
  1288: "Hell's Heaven", // 1 Hydra Charteris Base
};

export const HERO_ID_MAP = {
  1011: { name: "Hulk", role: ROLES.DEFENDER },
  1014: { name: "Punisher", role: ROLES.DUELIST },
  1015: { name: "Storm", role: ROLES.DUELIST },
  1016: { name: "Loki", role: ROLES.STRATEGIST },
  1017: { name: "Human Torch", role: ROLES.DUELIST },
  1018: { name: "Doctor Strange", role: ROLES.DEFENDER },
  1020: { name: "Mantis", role: ROLES.STRATEGIST },
  1021: { name: "Hawkeye", role: ROLES.DUELIST },
  1022: { name: "Captain America", role: ROLES.DEFENDER },
  1023: { name: "Rocket Raccoon", role: ROLES.STRATEGIST },
  1024: { name: "Hela", role: ROLES.DUELIST },
  1025: { name: "Cloak & Dagger", role: ROLES.STRATEGIST },
  1026: { name: "Black Panther", role: ROLES.DUELIST },
  1027: { name: "Groot", role: ROLES.DEFENDER },
  1029: { name: "Magik", role: ROLES.DUELIST },
  1030: { name: "Moon Knight", role: ROLES.DUELIST },
  1031: { name: "Luna Snow", role: ROLES.STRATEGIST },
  1032: { name: "Squirrel Girl", role: ROLES.DUELIST },
  1033: { name: "Black Widow", role: ROLES.DUELIST },
  1034: { name: "Iron Man", role: ROLES.DUELIST },
  1035: { name: "Venom", role: ROLES.DEFENDER },
  1036: { name: "Spider Man", role: ROLES.DUELIST },
  1037: { name: "Magneto", role: ROLES.DEFENDER },
  1038: { name: "Scarlet Witch", role: ROLES.DUELIST },
  1039: { name: "Thor", role: ROLES.DEFENDER },
  1040: { name: "Mister Fantastic", role: ROLES.DUELIST },
  1041: { name: "Winter Soldier", role: ROLES.DUELIST },
  1042: { name: "Peni Parker", role: ROLES.DEFENDER },
  1043: { name: "Star Lord", role: ROLES.DUELIST },
  1045: { name: "Namor", role: ROLES.DUELIST },
  1046: { name: "Adam Warlock", role: ROLES.STRATEGIST },
  1047: { name: "Jeff the Shark", role: ROLES.STRATEGIST },
  1048: { name: "Psylocke", role: ROLES.DUELIST },
  1049: { name: "Wolverine", role: ROLES.DUELIST },
  1050: { name: "Invisible Woman", role: ROLES.STRATEGIST },
  1051: { name: "The Thing", role: ROLES.DEFENDER },
  1052: { name: "Iron Fist", role: ROLES.DUELIST },
  1053: { name: "Emma Frost", role: ROLES.STRATEGIST },
  4016: { name: "Galacta Bot" },
  4018: { name: "Galacta Bot Plus" },
} as const;

export const RANKS = {
  ONE_ABOVE_ALL: {
    t: ["marvelrivals:rank.oneAboveAll", "One Above All"],
    imgKey: "rank_09",
    color: "#fd3f34",
  },
  ETERNITY: {
    t: ["marvelrivals:rank.eternity", "Eternity"],
    imgKey: "rank_08",
    color: "#eb19fc",
  },
  CELESTIAL: {
    t: ["marvelrivals:rank.celestial", "Celestial {{division}}"],
    imgKey: "rank_07",
    color: "#f84c28",
  },
  GRAND_MASTER: {
    t: ["marvelrivals:rank.gm", "Grand Master {{division}}"],
    imgKey: "rank_06",
    color: "#7f13f5",
  },
  DIAMOND: {
    t: ["marvelrivals:rank.diamond", "Diamond {{division}}"],
    imgKey: "rank_05",
    color: "#1188fd",
  },
  PLATINUM: {
    t: ["marvelrivals:rank.platinum", "Platinum {{division}}"],
    imgKey: "rank_04",
    color: "#08aac9",
  },
  GOLD: {
    t: ["marvelrivals:rank.gold", "Gold {{division}}"],
    imgKey: "rank_03",
    color: "#fba50e",
  },
  SILVER: {
    t: ["marvelrivals:rank.silver", "Silver {{division}}"],
    imgKey: "rank_02",
    color: "#75a7ba",
  },
  BRONZE: {
    t: ["marvelrivals:rank.bronze", "Bronze {{division}}"],
    imgKey: "rank_01",
    color: "#da8a5a",
  },
  // Not a real rank in Marvels
  UNRANKED: {
    t: ["common:rank.unranked", "Unranked"],
    imgKey: "rank_01",
    color: "var(--shade4)",
  },
} as const;

export const BADGE_MAP = new Map<
  number,
  {
    t: Translation;
    flare: BadgeProps["flare"];
    icon: LazyExoticComponent<SVGComponent>;
  }
>([
  [
    3,
    {
      flare: "Gold",
      t: ["marvelrivals:stats.hexaKill", "Hexa Kill"],
      icon: lazy(() => import("@/inline-assets/Badge_Tower_shape.svg")),
    },
  ],
  [
    5,
    {
      flare: "Silver",
      t: ["battles:overlay.lol.multiKill.quad", "Quadra Kill"],
      icon: lazy(() => import("@/inline-assets/Badge_Quadra_Icon.svg")),
    },
  ],
  [
    6,
    {
      flare: "Bronze",
      t: ["battles:overlay.lol.multiKill.triple", "Triple Kill"],
      icon: lazy(() => import("@/inline-assets/Badge_Tripple_Kill_Icon.svg")),
    },
  ],
  [
    7,
    {
      flare: "Silver",
      t: ["lol:build.playstyles.keystone.bestTank", "Best Tank"],
      icon: lazy(() => import("@/inline-assets/Badge_Armor_shape.svg")),
    },
  ],
  [
    8,
    {
      flare: "Silver",
      t: ["marvelrivals:stats.mostAssists", "Most Assists"],
      icon: lazy(() => import("@/inline-assets/Badge_Val_Team_Player.svg")),
    },
  ],
  [
    9,
    {
      flare: "Silver",
      t: ["marvelrivals:stats.bestDamage", "Best Damage"],
      icon: lazy(() => import("@/inline-assets/Badge_Sword_shape.svg")),
    },
  ],
  [
    11,
    {
      flare: "Silver",
      t: ["eft:leaderboard.sort.highestTotalKills", "Most Kills"],
      icon: lazy(() => import("@/inline-assets/Badge_Solo_Killer_Icon.svg")),
    },
  ],
  [
    12,
    {
      flare: "Silver",
      t: ["marvelrivals:stats.mostHeals", "Most Heals"],
      icon: lazy(() => import("@/inline-assets/Badge_Support_shape.svg")),
    },
  ],
  [
    41,
    {
      flare: "Gold",
      t: ["battles:overlay.lol.multiKill.penta", "Penta Kill"],
      icon: lazy(() => import("@/inline-assets/Badge_Penta_Kill_Icon.svg")),
    },
  ],
]);
